import { useRef, useLayoutEffect, memo, startTransition } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment/moment'

import { setValue } from 'Store/auth/reportSlice'
import { Tooltip } from 'Components/Ui'
import {
    frequencyOptions,
    locationOfTestingOptions,
    phasesOptions,
    productNameOptions,
    ratingSelectionOptions,
    transformerDefaultData,
    windingConnectionOptions,
    windingMarkingOptions,
    windingResistancUnitOptions,
    windingSortMarkingOptions,
    windingTypeOptions,
    yesNoOptions,
} from './options'

export function numberFormat(
    value,
    mindigits = 0,
    maxdigits = 2,
    style = 'decimal' // decimal, currency, percent, unit
) {
    value = style === 'percent' ? value / 100 : value
    return new Intl.NumberFormat('en-IN', {
        style: style,
        currency: 'INR',
        minimumFractionDigits: mindigits,
        maximumFractionDigits: maxdigits,
        // roundingMode: 'floor',
    })
        .format(value)
        .replaceAll(',', '')
}

export function userName(user) {
    return (
        <div className="flex items-center gap-3">
            <div className="relative w-full h-11 max-w-11">
                <img src={user.image_url + '?h=44&w=44&aspect=true&type=webp'} alt={user.name} />
            </div>
            <div className="w-full truncate">
                <p className="text-black dark:text-white">{user.name}</p>
                <p className="text-xs font-medium">{user.email}</p>
            </div>
        </div>
    )
}

export function userVerified(verified, check = false) {
    var className = ''
    var name = ''
    switch (verified) {
        case null:
            className = 'text-danger bg-danger'
            name = 'Not Verified'
            break
        default:
            className = 'text-success bg-success'
            name = 'Verified'
            break
    }
    if (check) return name
    return <div className={`inline-flex rounded-full bg-opacity-10 py-1 px-3 text-sm font-medium ${className}`}>{name}</div>
}

export function userStatus(status, check = false) {
    var className = ''
    var name = ''
    switch (status) {
        case 0:
            className = 'text-danger bg-danger'
            name = 'Inactive'
            break
        case 1:
            className = 'text-success bg-success'
            name = 'Active'
            break
        default:
            name = [
                {
                    id: 0,
                    label: 'Inactive',
                },
                {
                    id: 1,
                    label: 'Active',
                },
            ]
            break
    }
    if (check) return name
    return <div className={`inline-flex rounded-full bg-opacity-10 py-1 px-3 text-sm font-medium ${className}`}>{name}</div>
}

export function yesNo(type = '', check = false) {
    var className = ''
    var name = ''
    switch (type) {
        case 0:
            className = 'text-danger bg-danger'
            name = 'No'
            break
        case 1:
            className = 'text-success bg-success'
            name = 'Yes'
            break
        default:
            name = [
                {
                    id: 0,
                    label: 'No',
                },
                {
                    id: 1,
                    label: 'Yes',
                },
            ]
            break
    }
    if (check || !className) return name
    return <div className={`inline-flex rounded-full bg-opacity-10 py-1 px-3 text-sm font-medium ${className}`}>{name}</div>
}

export function transformerTestStatus(status, check = false) {
    var className = ''
    var name = ''
    switch (status) {
        case 1:
            className = 'text-success bg-success'
            name = 'Done'
            break
        case 0:
            className = 'text-danger bg-danger'
            name = 'Pending'
            break
        default:
            name = [
                {
                    id: 0,
                    label: 'Pending',
                },
                {
                    id: 1,
                    label: 'Done',
                },
            ]
            break
    }
    if (check) return name
    return <div className={`inline-flex rounded-full bg-opacity-10 py-1 px-3 text-sm font-medium ${className}`}>{name}</div>
}

export function transTestStatus(status, check = false, remarks = '') {
    var className = ''
    var name = ''
    switch (status) {
        case 0:
            className = 'text-danger bg-danger'
            name = 'Created'
            break
        case 1:
            className = 'text-warning bg-warning'
            name = 'WIP'
            break
        case 2:
            className = 'text-info bg-info'
            name = 'Pending'
            break
        case 3:
            className = 'text-success bg-success'
            name = 'Accepted'
            break
        default:
            className = 'text-danger bg-danger'
            name = 'Rejected'
            break
    }
    if (check) return name
    return (
        <div className={`inline-flex rounded-full bg-opacity-10 py-1 px-3 text-sm font-medium ${className}`}>
            {status === 4 && remarks ? <Tooltip title={remarks}>{name}</Tooltip> : name}
        </div>
    )
}

export function authorisedTestStatus(status, check = false) {
    var className = ''
    var name = ''
    switch (status) {
        case 0:
            className = 'text-success bg-success'
            name = 'Not required'
            break
        case 2:
            className = 'text-info bg-info'
            name = 'Pending'
            break
        case 3:
            className = 'text-success bg-success'
            name = 'Accepted'
            break
        default:
            className = 'text-danger bg-danger'
            name = 'Rejected'
            break
    }
    if (check) return name
    return <div className={`inline-flex rounded-full bg-opacity-10 py-1 px-3 text-sm font-medium ${className}`}>{name}</div>
}

export function inspectorsTestStatus(status, check = false) {
    var className = ''
    var name = ''
    switch (status) {
        case 2:
            className = 'text-info bg-info'
            name = 'Pending'
            break
        case 3:
            className = 'text-success bg-success'
            name = 'Accepted'
            break
        default:
            className = 'text-danger bg-danger'
            name = 'Rejected'
            break
    }
    if (check) return name
    return <div className={`inline-flex rounded-full bg-opacity-10 py-1 px-3 text-sm font-medium ${className}`}>{name}</div>
}

export function userRole(guard, check = false) {
    var className = ''
    var name = ''
    switch (guard) {
        case 'admin':
            className = 'text-danger bg-danger'
            name = 'Admin'
            break
        case 'user':
            className = 'text-success bg-success'
            name = 'User'
            break
        default:
            className = 'text-info bg-info'
            name = 'Inspector'
            break
    }
    if (check) return name
    return <div className={`inline-flex rounded-full bg-opacity-10 py-1 px-3 text-sm font-medium ${className}`}>{name}</div>
}

export function feedbackStatus(status, check = false) {
    var className = ''
    var name = ''
    switch (status) {
        case 1:
            className = 'text-success bg-success'
            name = 'Completed'
            break
        case 0:
            className = 'text-info bg-info'
            name = 'Pending'
            break
        default:
            name = [
                {
                    id: 0,
                    label: 'Pending',
                },
                {
                    id: 1,
                    label: 'Completed',
                },
            ]
            break
    }
    if (check) return name
    return <div className={`inline-flex rounded-full bg-opacity-10 py-1 px-3 text-sm font-medium ${className}`}>{name}</div>
}

export function calibrationDueOn(date) {
    if (!date) return null
    var className = moment(date).isSameOrAfter(moment(), 'day') ? 'text-success bg-success' : 'text-danger bg-danger'
    return (
        <div className={`inline-flex rounded-full bg-opacity-10 py-1 px-3 text-sm font-medium ${className}`}>
            {moment(date).format('ll')}
        </div>
    )
}

export function optionLable(value, options) {
    var name = options.find(({ id }) => id === value)?.label
    return name
}

export function isFormulaValid(formula, values = []) {
    const valueMap = [...values, ...transformerDefaultData].reduce((obj, item) => ({ ...obj, [item.id]: item.value }), {})
    try {
        let parsedFormula = formula
            .replace(/([a-zA-Z0-9_]+)/g, match => {
                if (/^[+-]?\d+(\.\d+)?$/.test(match)) return match
                return valueMap[match] ?? match
            })
            .replaceAll('^', '**')
            .replaceAll('[', '')
            .replaceAll(']', '')
            .replaceAll('sum', '')
        // eslint-disable-next-line
        eval(parsedFormula)
        return true
    } catch (error) {
        return false
    }
}

export function checkFormat(value, data_type, digits = 4) {
    if (data_type === '5') return value ? moment(value).format('DD/MM/YYYY') : ''
    if (data_type === '1') return numberFormat(value ?? 0, digits, digits)
    return value ?? ''
}

export function checkLabel(label, values, checkParameter) {
    return label
        .replace(/([a-zA-Z0-9_]+)(\[[^\]]+\])?/g, variableReplacer(false, values, checkParameter, 0))
        .replace(/\^\{(\d+)\}/g, (_, num) => {
            const superscripts = {
                0: '\u2070',
                1: '\u00B9',
                2: '\u00B2',
                3: '\u00B3',
                4: '\u2074',
                5: '\u2075',
                6: '\u2076',
                7: '\u2077',
                8: '\u2078',
                9: '\u2079',
            }
            return num
                .split('')
                .map(d => superscripts[d] || d)
                .join('')
        })
    // .replace(/\^\{([^}]+)\}/g, (_, expr) => `<sup>${expr}</sup>`)
}

export function formulaExecution(formula, values, checkParameter, colIndex, rowIndex) {
    values['COL_INDEX'] = colIndex
    values['ROW_INDEX'] = rowIndex
    const sumValues = arr =>
        Array.isArray(arr)
            ? arr.reduce((acc, item) => parseFloat(acc?.value ?? acc ?? 0) + parseFloat(item?.value ?? item ?? 0), 0)
            : Number(arr)
    const stdevValues = arr => {
        // eslint-disable-next-line
        const array = arr.split(',').map(item => eval(item ?? 0))
        const n = array.length
        if (n < 2) return 0
        const mean = array.reduce((a, b) => a + b) / n
        const variance = array.map(x => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / (n - 1)
        return Math.sqrt(variance)
    }

    try {
        var processedFormula = !formula.includes('includes') && formula.includes('[') ? checkLabel(formula, values) : formula
        if (['B_WDG_1_CORI'].includes(formula)) {
            processedFormula =
                colIndex === 0 || Number(values?.B_WDG_1_CORI[colIndex - 1]?.value ?? 0) === 0
                    ? 0
                    : Number(values?.B_WDG_1_CORI[colIndex]?.value ?? 0).toString() +
                      ' - ' +
                      Number(values?.B_WDG_1_CORI[colIndex - 1]?.value ?? 0).toString()
        }
        if (['B_WDG_2_CORI'].includes(formula)) {
            processedFormula =
                colIndex === 0 || Number(values?.B_WDG_2_CORI[colIndex - 1]?.value ?? 0) === 0
                    ? 0
                    : Number(values?.B_WDG_2_CORI[colIndex]?.value ?? 0).toString() +
                      ' - ' +
                      Number(values?.B_WDG_2_CORI[colIndex - 1]?.value ?? 0).toString()
        }
        if (['B_WDG_3_CORI'].includes(formula)) {
            processedFormula =
                colIndex === 0 || Number(values?.B_WDG_3_CORI[colIndex - 1]?.value ?? 0) === 0
                    ? 0
                    : Number(values?.B_WDG_3_CORI[colIndex]?.value ?? 0).toString() +
                      ' - ' +
                      Number(values?.B_WDG_3_CORI[colIndex - 1]?.value ?? 0).toString()
        }
        processedFormula = processedFormula
            .replace(/([a-zA-Z0-9_]+)/g, variableReplacer(true, values, checkParameter, colIndex, rowIndex))
            .replaceAll('^', '**')
            // eslint-disable-next-line
            .replace(/sum\((.*?)\)/g, (_, expr) => sumValues(eval(expr ?? 0)))
            .replace(/stdev\(\[(.*?)\]\)/g, (_, expr) => stdevValues(expr ?? []))
        if (/^\d{2}:\d{2}$/.test(processedFormula.trim())) {
            return processedFormula
        }
        // eslint-disable-next-line
        const result = eval(processedFormula)
        if (result.toString() === 'NaN') return 0
        return typeof result === 'string' ? result.replaceAll('"', '') : result
    } catch (error) {
        // console.log('error', error)
        return 0
    }
}

function variableReplacer(string, values, checkParameter, colIndex, rowIndex) {
    return function (match, key, index) {
        let value = values[key]
        const param = checkParameter?.(key)
        if (!value && checkParameter && param) {
            const paramValue = formulaExecution(param?.formula, values, checkParameter, colIndex, rowIndex)
            value = string ? paramValue : checkFormat(paramValue, param?.data_type, param?.digits)
        }
        const options = getOptions(key)
        if (!string && index) {
            const innerIndex = index.slice(1, -1).trim()
            if (/^\d+$/.test(innerIndex)) {
                colIndex = parseInt(innerIndex, 0)
            } else {
                colIndex = formulaExecution(innerIndex, values, checkParameter, colIndex, rowIndex)
            }
        }
        if (Array.isArray(value)) {
            if (typeof value[colIndex] === 'object') {
                return rowIndex >= 0 ? value[colIndex]?.[rowIndex]?.value ?? '' : value[colIndex]?.value ?? ''
            } else if (colIndex !== undefined) {
                return value[colIndex] ?? ''
            }
            return JSON.stringify(value)
        } else if (typeof value === 'object' && value !== null && 'value' in value) {
            return value.value
        } else if (options && options.length > 0) {
            const choice = options.find(option => option.id === parseInt(value))
            return choice ? (string ? `"${choice.label}"` : choice.label) : value
        } else if (value !== undefined) {
            if (string) {
                return /^-?\d+(\.\d+)?$/.test(value) ? value : `'${value}'`
            }
            if (value && param && param.parameterType === '1' && param.data_type === '4') {
                return moment(value).format('DD/MM/YYYY')
            }
            return value ?? ''
        }
        return match
    }
}

export function getOptions(field) {
    switch (field) {
        case 'wdg_1_sort_marking':
        case 'wdg_2_sort_marking':
        case 'wdg_3_sort_marking':
        case 'neutral_sort_marking':
            return windingSortMarkingOptions
        case 'wdg_1_marking':
        case 'wdg_2_marking':
        case 'wdg_3_marking':
        case 'neutral_marking':
            return windingMarkingOptions
        case 'no_of_phases':
            return phasesOptions
        case 'wdg_type':
            return windingTypeOptions
        case 'wdg_1_connection_type':
        case 'wdg_2_connection_type':
        case 'wdg_3_connection_type':
            return windingConnectionOptions
        case 'wdg_1_resistanc_unit':
        case 'wdg_2_resistanc_unit':
        case 'wdg_3_resistanc_unit':
            return windingResistancUnitOptions
        case 'rated_frequency':
            return frequencyOptions
        case 'product_name':
            return productNameOptions
        case 'location_of_testing':
            return locationOfTestingOptions
        case 'rating_selection':
            return ratingSelectionOptions
        case 'wdg_1_voltage_varation':
        case 'wdg_2_voltage_varation':
        case 'wdg_3_voltage_varation':
            return yesNoOptions
        default:
            return false
    }
}

export function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0 // Random number between 0 and 15
        const v = c === 'x' ? r : (r & 0x3) | 0x8 // Apply variant bits for UUID
        return v.toString(16) // Convert to hexadecimal
    })
}

export const CopyTax = ({ id = '', children, data_type = 0, isEdit = false }) => {
    var value = String(children ?? '').slice(data_type === '4' ? 3 : 0)
    return <EditText id={id} Div="span" isEdit={isEdit} value={value} />
}

export const FormatDateDDMMYYYY = date => {
    if (!date) return ''
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' }
    return new Date(date).toLocaleDateString('en-GB', options)
}

const EditTextBase = ({ id = null, Div = 'div', isEdit = false, value = '', className = '' }) => {
    const name = (id ? id + '-' : '') + value

    const storedValue = useSelector(state => state.report.details?.[name] ?? value)
    const dispatch = useDispatch()
    const ref = useRef(null)

    useLayoutEffect(() => {
        if (isEdit && ref.current) ref.current.textContent = storedValue
        // eslint-disable-next-line
    }, [isEdit])

    const handleBlur = e => {
        if (!name || !isEdit) return
        const text = e.currentTarget.textContent ?? ''
        if (storedValue === text) return
        startTransition(() => dispatch(setValue({ [name]: text })))
    }

    if (!isEdit) {
        return <Div className={className}>{storedValue}</Div>
    }

    return (
        <Div
            ref={ref}
            contentEditable
            suppressContentEditableWarning
            className={`focus:outline-none focus:ring-0 ${className}`}
            onBlur={handleBlur}
        />
    )
}

export const EditText = memo(EditTextBase)
